import React, { useState, useEffect, useRef } from "react";
import {
  addressToGroupAndPersonelAdds,
  getApartmentsDetails,
  getDistricts,
  getMahalleOzetListesi,
  getNeighborhoods,
  getStreets,
} from "../services/addressService";
import { getAllGroups } from "../services/groupService";
import "../styles/pages/_address-assignments.scss";
import { getAllPersonelUsers } from "../services/userService";
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';

// Mahalle özeti tablosunun sayısal sütunları; GENEL TOPLAM satırı bu alanların toplamıdır.
const MAHALLE_OZET_SUTUNLARI = [
  { baslik: "Toplam Hane", alan: "toplamHaneSayisi" },
  { baslik: "Aktif Hane", alan: "toplamAktifHaneSayisi" },
  { baslik: "Pasif Hane", alan: "toplamPasifHaneSayisi" },
  { baslik: "Toplam Sokak", alan: "toplamSokakSayisi" },
  { baslik: "Görev Planlaması Yapılmış Sokak", alan: "gorevPlanlamasiYapilmisSokakSayisi" },
  { baslik: "Gidilen Benzersiz Hane Sayısı", alan: "gidilenBenzersizHaneSayisi" },
  { baslik: "Gidilmeyen Benzersiz Hane Sayısı", alan: "gidilmeyenBenzersizHaneSayisi" },
  { baslik: "Gidilen Benzersiz Sokak Sayısı", alan: "gidilenBenzersizSokakSayisi" },
  { baslik: "Gidilmeyen Benzersiz Sokak Sayısı", alan: "gidilmeyenBenzersizSokakSayisi" },
];

const sayiBicimi = (n) => (n || 0).toLocaleString("tr-TR");

const AddressAssignments = ({ currentFilters = {} }) => {
  const toast = useRef(null);
  const [selectedFilters, setSelectedFilters] = useState(currentFilters);
  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [streets, setStreets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState({
    districts: false,
    neighborhoods: false,
    streets: false,
  });

  // Updated state to handle new response structure
  const [streetsData, setStreetsData] = useState([]);
  const [loadingApartments, setLoadingApartments] = useState(false);
  const [apartmentGroups, setApartmentGroups] = useState({});

  // Mahalle seçilmediğinde gösterilen mahalle bazlı özet (ilçe de seçilmediyse tüm ilçeler)
  const [mahalleOzet, setMahalleOzet] = useState([]);

  // Yeni state: Grup seçenekleri
  const [groupOptions, setGroupOptions] = useState([]);

  // Modal state'leri
  const [showHaneModal, setShowHaneModal] = useState(false);
  const [selectedApartment, setSelectedApartment] = useState(null);

  // Collapsible state for streets
  const [collapsedStreets, setCollapsedStreets] = useState(new Set());

  // Street search functionality
  const [streetSearchTerm, setStreetSearchTerm] = useState("");

  // Batch selection functionality
  const [selectedApartments, setSelectedApartments] = useState(new Set());
  const [batchGroup, setBatchGroup] = useState("");
  const [masterSelections, setMasterSelections] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isBatchAssigning, setIsBatchAssigning] = useState(false);

  // Toggle street collapse function
  const toggleStreetCollapse = (streetIndex) => {
    setCollapsedStreets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(streetIndex)) {
        newSet.delete(streetIndex);
      } else {
        newSet.add(streetIndex);
      }
      return newSet;
    });
  };

  // Toggle apartment selection
  const toggleApartmentSelection = (apartmentId) => {
    setSelectedApartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(apartmentId)) {
        newSet.delete(apartmentId);
      } else {
        newSet.add(apartmentId);
      }
      return newSet;
    });
  };

  // Toggle "select all streets" - selects/deselects every apartment in the
  // currently visible (filtered) street list
  const toggleSelectAllStreets = () => {
    const allApartmentIds = filteredStreetsData.flatMap(street =>
      street.apartmanlar.map(apt => apt.id)
    );

    if (allStreetsSelected) {
      // Clear all selections for the visible streets
      setSelectedApartments(prev => {
        const newSet = new Set(prev);
        allApartmentIds.forEach(id => newSet.delete(id));
        return newSet;
      });
      setMasterSelections({});
    } else {
      // Select every apartment in every visible street
      setSelectedApartments(prev => {
        const newSet = new Set(prev);
        allApartmentIds.forEach(id => newSet.add(id));
        return newSet;
      });
      const newMasterSelections = {};
      filteredStreetsData.forEach((_, idx) => {
        newMasterSelections[idx] = true;
      });
      setMasterSelections(newMasterSelections);
    }
  };

  // Toggle master selection for a street
  const toggleMasterSelection = (streetIndex) => {
    const street = filteredStreetsData[streetIndex];
    const apartmentIds = street.apartmanlar.map(apt => apt.id);
    
    setMasterSelections(prev => {
      const newMasterSelections = { ...prev };
      if (newMasterSelections[streetIndex]) {
        // Deselect all apartments in this street
        setSelectedApartments(prev => {
          const newSet = new Set(prev);
          apartmentIds.forEach(id => newSet.delete(id));
          return newSet;
        });
        delete newMasterSelections[streetIndex];
      } else {
        // Select all apartments in this street
        setSelectedApartments(prev => {
          const newSet = new Set(prev);
          apartmentIds.forEach(id => newSet.add(id));
          return newSet;
        });
        newMasterSelections[streetIndex] = true;
      }
      return newMasterSelections;
    });
  };

  // Handle batch group assignment
  const handleBatchGroupAssignment = async () => {
    if (!batchGroup || selectedApartments.size === 0) {
      alert("Lütfen bir grup seçin ve en az bir apartman seçin!");
      return;
    }

    setIsBatchAssigning(true);
    try {
      // Collect unique sokak names for selected apartments
      const selectedApartmentIds = Array.from(selectedApartments);
      const sokakSet = new Set();
      streetsData.forEach(street => {
        street.apartmanlar.forEach(apartment => {
          if (selectedApartmentIds.includes(apartment.id)) {
            sokakSet.add(street.sokak);
          }
        });
      });
      const sokakAdlari = Array.from(sokakSet).join(',');
      const response = await addressToGroupAndPersonelAdds(selectedFilters.district.label, selectedFilters.neighborhood.label, sokakAdlari, selectedUsers, batchGroup);
      // If the response is a file, trigger download
      if (response && response.data) {
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Format date as DD.MM.YYYY
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const formattedDate = `${day}.${month}.${year}`;
        a.download = `${selectedFilters.district.label}-${selectedFilters.neighborhood.label}-${formattedDate}.xlsx`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);
      }
      // Update local state to reflect the assignment
      setApartmentGroups(prev => {
        const newGroups = { ...prev };
        selectedApartments.forEach(apartmentId => {
          newGroups[apartmentId] = batchGroup;
        });
        return newGroups;
      });
      // Clear selections and input fields
      setSelectedApartments(new Set());
      setBatchGroup("");
      setSelectedUsers([]);
      setMasterSelections({});
      toast.current.show({
        severity: 'success',
        summary: 'Başarılı',
        detail: 'Grup ataması başarıyla tamamlandı!',
        life: 3000
      });
    } catch (error) {
      console.error("Toplu grup atama hatası:", error);
      alert("Grup atama işlemi başarısız oldu!");
    } finally {
      setIsBatchAssigning(false);
    }
  };

  // Hane modal'ını açma fonksiyonu
  const openHaneModal = (apartment) => {
    setSelectedApartment(apartment);
    setShowHaneModal(true);
  };

  // Hane modal'ını kapatma fonksiyonu
  const closeHaneModal = () => {
    setShowHaneModal(false);
    setSelectedApartment(null);
  };
  const fetchDays = async () => {
    try {
      const days = [
        "Pazartesi",
        "Salı",
        "Çarşamba",
        "Perşembe",
        "Cuma",
        "Cumartesi",
        "Pazar"
      ];
  
      const formatted = days.map(day => ({
        label: day,
        value: day
      }));
  
      setGroupOptions(formatted);
    } catch (error) {
      console.error("Günler alınırken hata:", error);
    }
  };
  
  // fetchGroups fonksiyonunu bileşen içinde tanımla
  const fetchGroups = async () => {
    try {
      // Örnek: Token'ı localStorage'dan veya başka bir yerden alıyoruz
      const response = await getAllGroups();

      if (Array.isArray(response)) {
        const formatted = response
          .filter((group) => !group.isDeleted)
          .map((g) => ({
            label: g.name,
            value: g.id,
          }));
        setGroupOptions(formatted);
      } else {
        console.warn("Grup verisi beklenen formatta değil:", response);
      }
    } catch (error) {
      console.error("Grup verisi çekilirken hata:", error);
    }
  };

  // İlk renderda ilçeleri, grupları ve kullanıcıları çek
  useEffect(() => {
    fetchDistricts();
    fetchDays();

    fetchUsers();
  }, []);

  // İlçeleri çek
  const fetchDistricts = async () => {
    try {
      setLoading((prev) => ({ ...prev, districts: true }));
      const response = await getDistricts();
      if (response.status === 200) {
        setDistricts(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch districts:", error);
    } finally {
      setLoading((prev) => ({ ...prev, districts: false }));
    }
  };

  // Kullanıcıları çek

  const fetchUsers = async () => {
    try {
      const response = await getAllPersonelUsers();
      setUsers(response);
    }
    catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  useEffect(() => {
    console.log('apartmentGroups: ', apartmentGroups);
  }, [apartmentGroups]);


  // Mahalleleri çek
  useEffect(() => {
    if (selectedFilters.district?.value) {
      fetchNeighborhoods(selectedFilters.district.value);
    } else {
      setNeighborhoods([]);
      setStreets([]);
      setSelectedFilters((prev) => ({
        ...prev,
        neighborhood: null,
        street: null,
      }));
    }
  }, [selectedFilters.district]);

  const fetchNeighborhoods = async (districtId) => {
    try {
      setLoading((prev) => ({ ...prev, neighborhoods: true }));
      const response = await getNeighborhoods(districtId);
      if (response.status === 200) {
        setNeighborhoods(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch neighborhoods:", error);
    } finally {
      setLoading((prev) => ({ ...prev, neighborhoods: false }));
    }
  };

  // Sokakları çek
  useEffect(() => {
    if (selectedFilters.neighborhood?.value) {
      fetchStreets(selectedFilters.neighborhood.value);
    } else {
      setStreets([]);
      setSelectedFilters((prev) => ({ ...prev, street: null }));
    }
  }, [selectedFilters.neighborhood]);

  const fetchStreets = async (neighborhoodId) => {
    try {
      setLoading((prev) => ({ ...prev, streets: true }));
      const response = await getStreets(neighborhoodId);
      if (response.status === 200) {
        setStreets(response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch streets:", error);
    } finally {
      setLoading((prev) => ({ ...prev, streets: false }));
    }
  };

  // Filtre değişimi için yardımcı fonksiyon
  const handleInputChange = (key, value) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      const newFilters = { ...selectedFilters };
      delete newFilters[key];
      setSelectedFilters(newFilters);
    } else {
      setSelectedFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
    
    // Clear street search and reset collapsed states when filters change
    setStreetSearchTerm("");
    setCollapsedStreets(new Set());
    setSelectedApartments(new Set());
    setBatchGroup("");
    setMasterSelections({});
    setMahalleOzet([]);
  };

  // Bir mahallenin sokak/apartman detayını çeker
  const fetchApartments = async (districtLabel, neighborhoodLabel) => {
    setLoadingApartments(true);
    setMahalleOzet([]);
    try {
      const neighborhood = encodeURIComponent(neighborhoodLabel);
      const district = encodeURIComponent(districtLabel);
      const response = await getApartmentsDetails(neighborhood, district);

      try {
        const json = response.data;

        // Handle the new response structure
        const streetsList = Array.isArray(json)
          ? json
          : json
          ? [json]
          : [];

        setStreetsData(streetsList);

        // Clear street search and reset all collapsed states when new data is loaded
        setStreetSearchTerm("");
        setCollapsedStreets(new Set());
        setSelectedApartments(new Set());
        setBatchGroup("");
        setMasterSelections({});
      } catch (jsonError) {
        console.error("❌ JSON parse edilemedi!", jsonError);
        setStreetsData([]);
      }
    } catch (error) {
      console.error("❗ fetch çağrısında hata:", error);
      setStreetsData([]);
    } finally {
      setLoadingApartments(false);
    }
  };

  // Mahalle seçili değilken: ilçenin (ilçe de yoksa tüm ilçelerin) mahalle bazlı hane/sokak özetini çeker
  const fetchMahalleOzet = async (districtLabel) => {
    setLoadingApartments(true);
    setStreetsData([]);
    try {
      const response = await getMahalleOzetListesi(districtLabel);
      setMahalleOzet(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error("❗ Mahalle özeti alınamadı:", error);
      setMahalleOzet([]);
    } finally {
      setLoadingApartments(false);
    }
  };

  // Özet tablosundan bir mahalleye tıklanınca o mahallenin detayına geçilir.
  // Detay çağrısı mahalle ADIyla çalıştığı için, mahalle dropdown'da bulunamasa da
  // (pasif/eksik mahalle kaydı) satır tıklaması çalışmaya devam eder.
  // Tüm ilçeler özetinde önce satırın ilçesi seçilir; mahalle o ilçenin listesinde aranır.
  const handleMahalleOzetClick = async (satir) => {
    let districtLabel = selectedFilters.district?.label;
    let mahalleListesi = neighborhoods;

    if (!districtLabel) {
      const ilce = districts.find((d) => d.ad?.trim() === satir.ilce);
      if (!ilce) return;

      districtLabel = ilce.ad;
      handleInputChange("district", { value: ilce.id, label: ilce.ad });
      try {
        const response = await getNeighborhoods(ilce.id);
        mahalleListesi = response.status === 200 ? response.data || [] : [];
      } catch (error) {
        console.error("Failed to fetch neighborhoods:", error);
        mahalleListesi = [];
      }
    }

    const eslesen = mahalleListesi.find((n) => n.ad === satir.mahalle);
    if (eslesen) {
      handleInputChange("neighborhood", { value: eslesen.id, label: eslesen.ad });
    }
    fetchApartments(districtLabel, satir.mahalle);
  };

  // Filtrele butonuna basıldığında apartmanları çek - Updated for new structure
  const handleFilterClick = async () => {
    // Mahalle seçilmediyse mahalle bazlı özet gösterilir: ilçe seçiliyse o ilçenin, değilse tüm ilçelerin.
    if (!selectedFilters.neighborhood) {
      await fetchMahalleOzet(selectedFilters.district?.label);
      return;
    }

    await fetchApartments(
      selectedFilters.district.label,
      selectedFilters.neighborhood.label
    );
  };

  useEffect(() => {
    console.log('streetsData: ', streetsData);
  }, [streetsData]);

  // Collapse all streets whenever streetsData changes
  useEffect(() => {
    if (streetsData.length > 0) {
      setCollapsedStreets(new Set(streetsData.map((_, idx) => idx)));
    } else {
      setCollapsedStreets(new Set());
    }
  }, [streetsData]);

  // Calculate total hane count in all streets
  const totalHaneCount = streetsData.reduce((sum, street) => sum + street.apartmanlar.reduce((aptSum, apt) => aptSum + (apt.haneler ? apt.haneler.length : 0), 0), 0);

  // Filter streets based on search term
  const filteredStreetsData = streetsData.filter(street => 
    street.sokak.toLowerCase().includes(streetSearchTerm.toLowerCase())
  );

  // Calculate the total number of hanes for selected apartments
  const selectedHaneCount = streetsData.reduce((total, street) => {
    return total + street.apartmanlar.reduce((aptSum, apartment) => {
      if (selectedApartments.has(apartment.id)) {
        return aptSum + (apartment.haneler ? apartment.haneler.length : 0);
      }
      return aptSum;
    }, 0);
  }, 0);

  // Helper: is a street's task planning done? Backend marks it via planDurumu === "var"
  const isStreetPlanned = (street) => street?.planDurumu === "var";

  // Whether every visible (filtered) apartment is currently selected
  const allStreetsSelected =
    filteredStreetsData.length > 0 &&
    filteredStreetsData.every(street =>
      street.apartmanlar.length > 0 &&
      street.apartmanlar.every(apt => selectedApartments.has(apt.id))
    );

  // Summary statistics for the filtered/visible streets (dynamic with search + region)
  const summaryStats = filteredStreetsData.reduce(
    (acc, street) => {
      const streetHane = street.apartmanlar.reduce(
        (sum, apt) => sum + (apt.haneler ? apt.haneler.length : 0),
        0
      );
      const streetPasif = street.apartmanlar.reduce(
        (sum, apt) => sum + (apt.bloklananlar || 0),
        0
      );
      acc.toplamHane += streetHane;
      acc.pasifHane += streetPasif;
      acc.aktifHane += streetHane - streetPasif;
      acc.toplamSokak += 1;
      if (isStreetPlanned(street)) acc.planlananSokak += 1;
      return acc;
    },
    { toplamHane: 0, aktifHane: 0, pasifHane: 0, toplamSokak: 0, planlananSokak: 0 }
  );

  // Özet ilçe seçilmeden yüklendiyse tüm ilçelerin mahalleleri listelenir; İlçe sütunu gösterilir.
  // Filtre değişince özet temizlendiği için ekrandaki özet her zaman güncel filtreye aittir.
  const ozetTumIlceler = !selectedFilters.district;
  const mahalleOzetToplam = Object.fromEntries(
    MAHALLE_OZET_SUTUNLARI.map(({ alan }) => [
      alan,
      mahalleOzet.reduce((toplam, satir) => toplam + (satir[alan] || 0), 0),
    ])
  );

  // Ref for batch assignment container
  const batchAssignmentRef = useRef(null);
  const [isBatchAbsolute, setIsBatchAbsolute] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setIsBatchAbsolute(true);
      } else {
        setIsBatchAbsolute(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="people-container neighborhood-container">
      <div className="filter-form" style={{padding: "0"}}>
        <div className="form-row">
          <div className="form-group">
            <label>İlçe</label>
            <select
              value={selectedFilters.district?.value || ""}
              onChange={(e) => {
                const selectedDistrict = districts.find(
                  (d) => d.id === e.target.value
                );
                handleInputChange(
                  "district",
                  selectedDistrict
                    ? {
                        value: selectedDistrict.id,
                        label: selectedDistrict.ad,
                      }
                    : null
                );
              }}
              className="filter-input"
              disabled={loading.districts}
            >
              <option value="">İlçe Seçiniz</option>
              {districts.map((district) => (
                <option key={district.id} value={district.id}>
                  {district.ad}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Mahalle</label>
            <select
              value={selectedFilters.neighborhood?.value || ""}
              onChange={(e) => {
                const selectedNeighborhood = neighborhoods.find(
                  (n) => n.id === e.target.value
                );
                handleInputChange(
                  "neighborhood",
                  selectedNeighborhood
                    ? {
                        value: selectedNeighborhood.id,
                        label: selectedNeighborhood.ad,
                      }
                    : null
                );
              }}
              className="filter-input"
              disabled={loading.neighborhoods || !selectedFilters.district}
            >
              <option value="">Mahalle Seçiniz</option>
              {neighborhoods.map((neighborhood) => (
                <option key={neighborhood.id} value={neighborhood.id}>
                  {neighborhood.ad}
                </option>
              ))}
            </select>
          </div>

          <div
            className="form-group address-assignments-filter-button-container"
            style={{ minWidth: "100px", alignSelf: "flex-end" }}
          >
            <button
              type="button"
              className="neighborhood-filter-submit-button"
              onClick={handleFilterClick}
              style={{ width: "100%" }}
              disabled={loadingApartments}
            >
              {loadingApartments ? "Yükleniyor..." : "Filtrele"}
            </button>
            <a className="neighborhood-gorev-planlari-link" href="/gorev-planlari">Görev Planları</a>
          </div>
        </div>
      </div>

      {/* Mahalle Özeti - mahalle seçilmediğinde; ilçe de seçilmediyse tüm ilçeler */}
      {mahalleOzet.length > 0 && (
        <div className="streets-container">
          <div className="street-section">
            <h3 className="street-title">
              <div className="street-center">
                {ozetTumIlceler ? "Tüm İlçeler" : selectedFilters.district?.label} - Mahalle Özeti ({mahalleOzet.length} mahalle)
              </div>
            </h3>
            <table className="apartments-table">
              <thead>
                <tr>
                  {ozetTumIlceler && <th>İlçe</th>}
                  <th>Mahalle</th>
                  {MAHALLE_OZET_SUTUNLARI.map(({ baslik }) => (
                    <th key={baslik}>{baslik}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mahalleOzet.map((satir) => (
                  <tr
                    key={`${satir.ilce}|${satir.mahalle}`}
                    className="hane-count-cell"
                    style={{ cursor: "pointer" }}
                    title="Mahalle detayına git"
                    onClick={() => handleMahalleOzetClick(satir)}
                  >
                    {ozetTumIlceler && <td className="mahalle-ozet-ilce">{satir.ilce}</td>}
                    <td>{satir.mahalle}</td>
                    {MAHALLE_OZET_SUTUNLARI.map(({ alan }) => (
                      <td key={alan}>{sayiBicimi(satir[alan])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="mahalle-ozet-toplam">
                  <td>GENEL TOPLAM</td>
                  {ozetTumIlceler && <td>{mahalleOzet.length} Mahalle</td>}
                  {MAHALLE_OZET_SUTUNLARI.map(({ alan }) => (
                    <td key={alan}>{sayiBicimi(mahalleOzetToplam[alan])}</td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Özet İstatistik Paneli */}
      {streetsData.length > 0 && (
        <div className="street-stats-panel">
          <div className="street-stat-item">
            <span className="street-stat-label">Toplam Hane Sayısı</span>
            <span className="street-stat-value">{summaryStats.toplamHane}</span>
          </div>
          <div className="street-stat-item">
            <span className="street-stat-label">Toplam Aktif Hane Sayısı</span>
            <span className="street-stat-value">{summaryStats.aktifHane}</span>
          </div>
          <div className="street-stat-item">
            <span className="street-stat-label">Toplam Pasif Hane Sayısı</span>
            <span className="street-stat-value">{summaryStats.pasifHane}</span>
          </div>
          <div className="street-stat-item">
            <span className="street-stat-label">Toplam Sokak Sayısı</span>
            <span className="street-stat-value">{summaryStats.toplamSokak}</span>
          </div>
          <div className="street-stat-item">
            <span className="street-stat-label">Görev Planlaması Yapılmış Sokak Sayısı</span>
            <span className="street-stat-value">{summaryStats.planlananSokak}</span>
          </div>
        </div>
      )}

      {/* Street Search */}
      {streetsData.length > 0 && (
        <div className="street-search-container">
          <div className="street-search-row">
            <div className="street-search-group">
              <label>Sokak Ara:</label>
              <input
                type="text"
                placeholder="Sokak adını yazın..."
                value={streetSearchTerm}
                onChange={(e) => setStreetSearchTerm(e.target.value)}
                className="street-search-input"
              />
            </div>
            {streetSearchTerm && (
              <button
                type="button"
                onClick={() => setStreetSearchTerm("")}
                className="clear-search-button"
              >
                Temizle
              </button>
            )}
          </div>
        </div>
      )}

      {/* Batch Group Assignment */}
      {streetsData.length > 0 && (
        <div ref={batchAssignmentRef} className={`batch-assignment-container${isBatchAbsolute ? " batch-absolute" : ""}`}>
          <div className="batch-assignment-row">
            <div className="batch-assignment-group hidden">
              <label>Görev atama işlemi</label>
              <select
                value={batchGroup}
                onChange={(e) => setBatchGroup(e.target.value)}
                className="batch-group-select"
              >
                <option value="">Gün Seçiniz</option>
                {groupOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
              </select>
            </div>
            <div className="batch-assignment-group">
              <label>Kullanıcı Seçiniz:</label>
              <MultiSelect
                value={selectedUsers}
                options={users.map(user => ({ label: user.adSoyad, value: user.id }))}
                onChange={e => setSelectedUsers(e.value)}
                optionLabel="label"
                placeholder="Kullanıcı Seçiniz"
                className="filter-input batch-group-select"
                filter
                display="chip"
              />
            </div>
            <button
              type="button"
              onClick={handleBatchGroupAssignment}
              className="batch-assign-button"
              disabled={!batchGroup || selectedApartments.size === 0 || isBatchAssigning}
              dangerouslySetInnerHTML={{ __html: isBatchAssigning ? `<span class='spinner' style='display:inline-block;width:18px;height:18px;border:2px solid #fff;border-top:2px solid #18964e;border-radius:50%;animation:spin 1s linear infinite;vertical-align:middle;margin-right:8px;'></span> Atanıyor...` : `Seçilenleri Ata <br/>(${selectedApartments.size} Apartman, ${selectedHaneCount} Hane)` }}
            />
          </div>
        </div>
      )}

      {/* Streets and Apartments Tables */}
      {filteredStreetsData.length > 0 && (
                <div className="streets-container">
          {/* Tüm Sokakları Seç */}
          <div className="select-all-streets-row">
            <label className="select-all-streets-label">
              <input
                type="checkbox"
                checked={allStreetsSelected}
                onChange={toggleSelectAllStreets}
                className="master-checkbox"
              />
              Tüm Sokakları Seç
            </label>
          </div>
          {filteredStreetsData.map((street, filteredIndex) => {
            // Find the original index in streetsData for collapse functionality
            const originalIndex = streetsData.findIndex(s => s.sokak === street.sokak);
            const planned = isStreetPlanned(street);
            return (
              <div
                key={originalIndex}
                className={`street-section ${planned ? "street-planned" : "street-unplanned"}`}
              >
                <h3 className="street-title">
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      checked={masterSelections[filteredIndex] || false}
                      onChange={(e) => toggleMasterSelection(filteredIndex)}
                      className="master-checkbox"
                    />
                  </div>
                  <div className="street-center" onClick={() => toggleStreetCollapse(originalIndex)}>
                    <span className="collapse-icon">{collapsedStreets.has(originalIndex) ? "▶" : "▼"}</span>
                    Sokak: {street.sokak} (Toplam Hane: {street.apartmanlar.reduce((sum, apt) => sum + (apt.haneler ? apt.haneler.length : 0), 0)}, Aktif Hane: {street.apartmanlar.reduce((sum, apt) => sum + (apt.haneler ? apt.haneler.length : 0), 0) - street.apartmanlar.reduce((sum, apt) => sum + (apt.bloklananlar || 0), 0)}, Pasif Hane: {street.apartmanlar.reduce((sum, apt) => sum + (apt.bloklananlar || 0), 0)})
                  </div>
                </h3>
                {!collapsedStreets.has(originalIndex) && (
                <table className="apartments-table">
                  <thead>
                    <tr>
                      <th>
                        <input
                          type="checkbox"
                          checked={masterSelections[filteredIndex] || false}
                          onChange={() => toggleMasterSelection(filteredIndex)}
                          className="master-checkbox"
                        />
                      </th>
                      <th>Apartman Adı</th>
                      <th>Hane Sayısı</th>
                    </tr>
                  </thead>
                  <tbody>
                    {street.apartmanlar.map((apartment) => {
                      const isGroupSelected = apartmentGroups[apartment.id] || apartment.grup || "";
                      const isSelected = selectedApartments.has(apartment.id);

                      return (
                        <tr key={apartment.id} className={isSelected ? "selected-row" : ""}>
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleApartmentSelection(apartment.id)}
                              className="apartment-checkbox"
                            />
                          </td>
                          <td>{apartment.apartmanAdi}</td>
                          <td 
                            className="hane-count-cell"
                            onClick={() => openHaneModal(apartment)}
                          >
                            {apartment.haneler.length}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
             )}
           </div>
         );
       })}
       </div>
     )}

      {streetsData.length === 0 && mahalleOzet.length === 0 && !loadingApartments && (
        <p style={{ marginTop: "20px", fontStyle: "italic", color: "#666" }}>
          Seçilen mahallede henüz kayıtlı sokak bulunamadı.
        </p>
      )}

      {/* Grup Özeti Tablosu - Fixed Position */}
      {streetsData.length > 0 && (
        <div className="group-summary-container">
          <table className="group-summary-table">
            <thead>
              <tr>
                <th>Toplam Hane Sayısı</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{totalHaneCount}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Hane Detay Modal */}
      {showHaneModal && selectedApartment && (
        <div className="hane-modal-overlay" onClick={closeHaneModal}>
          <div className="hane-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="hane-modal-header">
              <h3>{selectedApartment.apartmanAdi} Apartmanı - Haneler</h3>
              <button className="hane-modal-close" onClick={closeHaneModal}>
                ×
              </button>
            </div>
            <div className="hane-modal-body">
              {selectedApartment.haneler && selectedApartment.haneler.length > 0 ? (
                <div className="hane-list">
                  {selectedApartment.haneler.map((hane, index) => {
                    // Pasif: projeye katılmıyor / geçici katılmıyor / adres hatalı (bloklananlar ile aynı tanım)
                    const pasif = (selectedApartment.pasifHaneler || []).includes(hane);
                    return (
                      <div key={index} className={`hane-item ${pasif ? "hane-item-pasif" : "hane-item-aktif"}`}>
                        <span>{hane}</span>
                        <span className={`hane-durum ${pasif ? "hane-durum-pasif" : "hane-durum-aktif"}`}>
                          <i className={`pi ${pasif ? "pi-times-circle" : "pi-check-circle"}`} />
                          {pasif ? "Pasif" : "Aktif"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="no-hane-message">Bu apartmanda henüz hane kaydı bulunmamaktadır.</p>
              )}
            </div>
          </div>
        </div>
      )}
      <Toast ref={toast} />
    </div>
  );
};

export default AddressAssignments;
